/**
 * WeeklySelectionSummary component
 * Display summary of meal selections vs availability
 */

import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import type { AvailableMeal } from '@/types/api';

interface WeeklySelectionSummaryProps {
  selectedCount: number;
  requiredCount: number;
  selectedMeals?: AvailableMeal[];
  onSave: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  hasChanges?: boolean;
}

/**
 * WeeklySelectionSummary component
 *
 * Features:
 * - Show X/Y meals selected
 * - Visual progress indicator
 * - Save/Cancel buttons
 * - Warning if under/over selected
 * - Accessible status updates
 *
 * @example
 * ```tsx
 * <WeeklySelectionSummary
 *   selectedCount={8}
 *   requiredCount={10}
 *   onSave={handleSave}
 *   isSaving={false}
 *   hasChanges={true}
 * />
 * ```
 */
export function WeeklySelectionSummary({
  selectedCount,
  requiredCount,
  selectedMeals = [],
  onSave,
  onCancel,
  isSaving = false,
  hasChanges = false,
}: WeeklySelectionSummaryProps) {
  const { t } = useTranslation();

  // Aggregate tags from selected meals
  const aggregatedTags = useMemo(() => {
    const tagCounts: Record<string, number> = {};

    selectedMeals.forEach((meal) => {
      meal.tags.forEach((tag) => {
        if (!tag.isHidden) {
          tagCounts[tag.tag] = (tagCounts[tag.tag] || 0) + 1;
        }
      });
    });

    // Sort by count (descending) then alphabetically
    return Object.entries(tagCounts)
      .sort(([, countA], [, countB]) => {
        if (countB !== countA) return countB - countA;
        return Object.entries(tagCounts)[0][0].localeCompare(
          Object.entries(tagCounts)[1]?.[0] || ''
        );
      });
  }, [selectedMeals]);

  // Filter cheat meals (meals with "cheat meal" or similar tag)
  const cheatMeals = useMemo(() => {
    return selectedMeals.filter((meal) =>
      meal.tags.some(
        (tag) =>
          tag.tag.toLowerCase().includes('cheat') ||
          tag.tag.toLowerCase().includes('écart') ||
          tag.tag.toLowerCase().includes('indulgence')
      )
    );
  }, [selectedMeals]);

  const isUnderSelected = selectedCount < requiredCount;
  const isOverSelected = selectedCount > requiredCount;
  const isExactMatch = selectedCount === requiredCount;
  const percentComplete = requiredCount > 0 ? (selectedCount / requiredCount) * 100 : 0;

  // Determine status message and color
  let statusMessage = '';
  let statusColor = 'text-gray-600';
  let bgColor = 'bg-gray-50';

  if (isExactMatch && selectedCount > 0) {
    statusMessage = t('weeklySelection.selectionsCount', { count: selectedCount });
    statusColor = 'text-green-700';
    bgColor = 'bg-green-50';
  } else if (isUnderSelected) {
    const remaining = requiredCount - selectedCount;
    statusMessage = t('weeklySelection.requiredCount', { count: remaining });
    statusColor = 'text-yellow-700';
    bgColor = 'bg-yellow-50';
  } else if (isOverSelected) {
    const extra = selectedCount - requiredCount;
    statusMessage = t('weeklySelection.selectedVsRequired', { selected: extra, required: 0 });
    statusColor = 'text-blue-700';
    bgColor = 'bg-blue-50';
  }

  const canSave = selectedCount > 0 && hasChanges && !isSaving;

  return (
    <div className={`${bgColor} rounded-lg p-6 border-2 border-gray-200 sticky top-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{t('weeklySelection.selectionsCount', { count: selectedCount })}</h3>
        <div className="text-right">
          <div className="text-3xl font-bold text-gray-900">
            <span className={selectedCount > 0 ? 'text-primary-600' : 'text-gray-400'}>
              {selectedCount}
            </span>
            <span className="text-gray-400 text-xl"> / </span>
            <span className="text-gray-600">{requiredCount}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{t('menu.meals')}</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              isExactMatch && selectedCount > 0
                ? 'bg-green-500'
                : isOverSelected
                  ? 'bg-blue-500'
                  : 'bg-primary-600'
            }`}
            style={{
              width: `${Math.min(percentComplete, 100)}%`,
            }}
            role="progressbar"
            aria-valuenow={selectedCount}
            aria-valuemin={0}
            aria-valuemax={requiredCount}
            aria-label={`${selectedCount} of ${requiredCount} meals selected`}
          />
        </div>
      </div>

      {/* Status message */}
      {statusMessage && (
        <div
          className={`mb-4 p-3 rounded-md ${statusColor} text-sm font-medium`}
          role="status"
          aria-live="polite"
        >
          {statusMessage}
        </div>
      )}

      {/* Info text */}
      <p className="text-sm text-gray-600 mb-4">
        {t('weeklySelection.selectedVsRequired', { selected: selectedCount, required: requiredCount })}
      </p>

      {/* Tags aggregation */}
      {aggregatedTags.length > 0 && (
        <div className="mb-4 pb-4 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">
            {t('weeklySelection.selectedTags')}
          </p>
          <div className="flex flex-wrap gap-2">
            {aggregatedTags.map(([tag, count]) => (
              <span
                key={tag}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                title={`${tag}: ${count} meal(s)`}
              >
                {tag}
                <span className="ml-1.5 text-xs opacity-75">({count})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Cheat meals list */}
      {cheatMeals.length > 0 && (
        <div className="mb-4 pb-4 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">
            {t('weeklySelection.cheatMeals')}
          </p>
          <div className="space-y-1">
            {cheatMeals.map((meal) => (
              <div
                key={meal.id}
                className="flex items-start gap-2 p-2 rounded bg-orange-50 border-l-2 border-orange-400"
              >
                <svg
                  className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-sm text-orange-900 font-medium">{meal.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          className={`
            w-full py-3 px-4 rounded-lg font-medium text-white transition-colors
            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
            ${
              canSave
                ? 'bg-primary-700 hover:bg-primary-800'
                : 'bg-gray-300 cursor-not-allowed'
            }
          `}
          aria-label={
            isSaving
              ? t('app.saving')
              : !hasChanges
                ? 'No changes to save'
                : selectedCount === 0
                  ? 'Please select at least one meal'
                  : t('app.save')
          }
        >
          {isSaving ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('app.saving')}
            </span>
          ) : (
            t('app.save')
          )}
        </button>

        {onCancel && hasChanges && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="w-full py-2 px-4 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            {t('app.cancel')}
          </button>
        )}
      </div>

      {/* Help text */}
      {hasChanges && (
        <p className="mt-3 text-xs text-gray-500 text-center">
          {t('weeklySelection.saveSuccessful')}
        </p>
      )}
    </div>
  );
}
